import { useState, useEffect } from "react";
import Card from "components/card";
import { getAdvertisementDataApi, editAdvertisementApi, addAdvertisementApi, deleteAdvertisementApi, toggleAdvertisementStatusApi } from "Services/allApi"; 
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Swal from "sweetalert2";

const AdvertisementCard = ({ extra, image }) => {
  const [advertisements, setAdvertisements] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [currentAd, setCurrentAd] = useState({});
  const [loading, setLoading] = useState(true); 

  const fetchAdvertisements = async () => {
    try {
      const response = await getAdvertisementDataApi();
      if (!response.error) {
        setAdvertisements(response.data);
      } else {
        console.error(response.message);
        toast.error("Failed to fetch data");
      }
    } catch (error) {
      console.error("Error fetching advertisements:", error);
      
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdvertisements();
  }, []);

  const handleToggle = async (adId) => {
    try {
      const response = await toggleAdvertisementStatusApi(adId); 
      if (!response.error) {
        setAdvertisements((prevAdvertisements) =>
          prevAdvertisements.map((ad) =>
            ad.id === adId ? { ...ad, disply: !ad.disply } : ad
          )
        );
        await fetchAdvertisements();
        toast.success('Advertisement status toggled successfully!');
      } else {
        console.error(response.message);
        toast.error('Failed to toggle advertisement status.');
      }
    } catch (error) {
      console.error("Error toggling advertisement status:", error);
      toast.error('Error toggling advertisement status.');
    }
  };

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    const allowedTypes = ["image/jpeg", "image/png", "image/gif"]; 
    const MAX_FILE_SIZE = 5 * 1024 * 1024;

    if (file) {
      if (!allowedTypes.includes(file.type)) {
        toast.error("Unsupported file type. Please upload a valid image (jpeg,png,gif).");
        return;
      }
      if (file.size > MAX_FILE_SIZE) {
        toast.error("File size is too large. Please select a file smaller than 5MB.");
        return;
      }

      setCurrentAd({ ...currentAd, imageFile: file });

      const reader = new FileReader();
      reader.onload = (e) => {
        setCurrentAd((prev) => ({ ...prev, imagePreview: e.target.result }));
      };
      try {
        reader.readAsDataURL(file);
      } catch (error) {
        console.error("Error reading file:", error);
        toast.error("Error reading the file. Please try a different file.");
      }
    }
  };

  const handleEdit = (ad) => {
    setCurrentAd(ad);
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    if (
      !currentAd.heading ||
      !currentAd.description ||
      !currentAd.btn_url ||
      !currentAd.btn_content ||
      (isAddModalOpen && !currentAd.imageFile)
    ) {
      toast.error('Please fill in all required fields before saving.');
      return;
    }
  
    const formData = new FormData();
    formData.append("heading", currentAd.heading);
    formData.append("description", currentAd.description);
    formData.append("btn_url", currentAd.btn_url);
    formData.append("btn_content", currentAd.btn_content);
    
    // Only append image if it's provided
    if (currentAd.imageFile instanceof File) {
      formData.append("image", currentAd.imageFile);
    }
  
    try {
      if (isAddModalOpen) {
        const response = await addAdvertisementApi(formData);
        if (!response.error) {
          setAdvertisements((prevAdvertisements) => [...prevAdvertisements, response.data]);
          toast.success('Advertisement added successfully!');
        } else {
          console.error(response.message);
          toast.error('Failed to add advertisement.');
        }
      } else if (currentAd?.id) {
        const response = await editAdvertisementApi(currentAd.id, formData);
        if (!response.error) {
          setAdvertisements(
            advertisements.map((ad) => (ad.id === currentAd.id ? response.data : ad))
          );
          toast.success('Advertisement updated successfully!');
        } else {
          console.error(response.message);
          toast.error('Failed to update advertisement.');
        }
      }
    } catch (error) {
      console.error("Error saving advertisement:", error);
      toast.error('Error saving advertisement.');
    }
  
    setIsModalOpen(false);
    setIsAddModalOpen(false);
  };
  


  const handleModalClose = () => {
    setIsModalOpen(false);
    setIsAddModalOpen(false);
  };

  const handleAdd = () => {
    setCurrentAd({ heading: "", description: "", btn_url: "", btn_content: "", image: null });
    setIsAddModalOpen(true);
  };

  const handleDelete = async (adId) => {
    Swal.fire({
      title: "Are you sure?",
      text: "Do you want to delete this advertisement?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",                          
      confirmButtonText: "Yes, delete it!",
      customClass: {
        popup: 'dark:bg-gray-800 dark:text-white',
        title: 'dark:text-white',
        confirmButton: 'dark:bg-blue-500 dark:hover:bg-blue-700 dark:text-white',
        cancelButton: 'dark:bg-red-500 dark:hover:bg-red-700 dark:text-white'
    }
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await deleteAdvertisementApi(adId);
          if (!response.error) {
            setAdvertisements(advertisements.filter((ad) => ad.id !== adId));
            toast.success("Advertisement deleted successfully!");
          } else {
            console.error(response.message);
            toast.error("Failed to delete advertisement.");
          }
        } catch (error) {
          console.error("Error deleting advertisement:", error);
          toast.error("Error deleting advertisement.");
        }
      }
    });
  };
  

  return (
    <>
      <div className="mb-4 flex justify-end mt-4">
        <button
          onClick={handleAdd}
          className="bg-purple-800 text-white px-4 py-2 rounded-lg font-medium text-sm hover:bg-purple-700"
        >
          Add Advertisement
        </button>
      </div>
      <div className="z-20 grid grid-cols-1 gap-5 md:grid-cols-3">
  {loading ? (
    <div className="w-full text-center text-purple-800">Loading advertisements...</div>
  ) : advertisements.length === 0 ? (
    <div className="w-full text-center text-red-500">
      No advertisements available...
    </div>
  ) : (
    advertisements
      .sort((a, b) => (b.disply === true ? 1 : 0) - (a.disply === true ? 1 : 0)) 
      .map((ad) => (
        <Card
          key={ad.id}
          extra={`flex flex-col w-full h-full !p-4 3xl:p-![18px] bg-white ${extra}`}
        >
          <div className="h-full w-full">
            <div className="relative w-full h-56 mb-4">
              <img
                src={ad.image || image}
                alt="Advertisement"
                className="absolute top-0 left-0 w-full h-full object-cover rounded-xl"
              />
            </div>
            <div className="mb-4 w-full">
              <p className="text-lg font-bold text-black-700 dark:text-white">
                {ad?.heading || "Title"}
              </p>
            </div>
            <div className="mb-4 w-full">
              <p className="text-sm text-black-600 dark:text-gray-300">
                {ad?.description || "Description"}
              </p>
            </div>
            <div className="mb-4 w-full">
              <a
                href={ad.btn_url || "#"}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-blue-500 dark:text-blue-300"
              >
                {ad.btn_content || "Button Text"}
              </a>
            </div>
            <div className="mt-4">
              <button
                onClick={() => handleEdit(ad)}
                className="w-full rounded-lg bg-purple-800 px-4 py-2 text-white font-medium text-sm transition duration-200 hover:bg-purple-700"
              >
                Edit
              </button>
              <div className="flex justify-between mt-3">
                <label className="inline-flex items-center cursor-pointer mt-3">
                  <input
                    type="checkbox"
                    checked={ad.disply}
                    onChange={() => handleToggle(ad.id)}
                    className="sr-only peer"
                  />
                  <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 dark:peer-focus:ring-purple-800 rounded-full peer dark:bg-purple-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-purple-600 dark:bg-white "></div>
                </label>
                <button
                  onClick={() => handleDelete(ad.id)}
                  className="rounded-lg bg-purple-800 px-4 py-2 text-white font-medium text-sm transition duration-200 hover:bg-red-600"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </Card>
      ))
  )}
</div>

      {(isModalOpen || isAddModalOpen) && (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-800 bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded-xl w-1/2 dark:bg-gray-800 dark:text-white">
            <h2 className="text-lg font-semibold text-gray-700">
              {isAddModalOpen ? "Add Advertisement" : "Edit Advertisement"}
            </h2>
            <div className="mt-4">
            <div className="relative mb-4">
                <label className="block text-sm text-gray-700">Image</label>
                <div className="relative w-full h-56 mb-4">
                  <img
                    src={currentAd?.imagePreview || currentAd?.image || image}
                    alt=""
                    className="absolute top-0 left-0 w-full h-full object-contain rounded-xl"
                  />
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="absolute top-0 left-0 w-full h-full opacity-0 cursor-pointer"
                  />
                </div>
              </div>
              <div className="mb-4">
                <label className="block text-sm text-gray-700">Heading</label>
                <input
                  type="text"
                  value={currentAd.heading || ""}
                  onChange={(e) => setCurrentAd({ ...currentAd, heading: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded-md dark:bg-[#0b14374d] dark:border-gray-600 dark:text-white"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm text-gray-700">Description</label>
                <textarea
                  value={currentAd.description || ""}
                  onChange={(e) =>
                    setCurrentAd({ ...currentAd, description: e.target.value })
                  }
                  className="w-full p-2 border border-gray-300 rounded-md dark:bg-[#0b14374d] dark:border-gray-600 dark:text-white"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm text-gray-700">Button URL</label>
                <input
                  type="text"
                  value={currentAd.btn_url || ""}
                  onChange={(e) => setCurrentAd({ ...currentAd, btn_url: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded-md dark:bg-[#0b14374d] dark:border-gray-600 dark:text-white"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm text-gray-700">Button Text</label>
                <input
                  type="text"
                  value={currentAd.btn_content || ""}
                  onChange={(e) =>
                    setCurrentAd({ ...currentAd, btn_content: e.target.value })
                  }
                  className="w-full p-2 border border-gray-300 rounded-md dark:bg-[#0b14374d] dark:border-gray-600 dark:text-white"
                />
              </div>
              <div className="flex justify-end mt-4">
                <button
                  onClick={handleSave}
                  className="bg-purple-800 text-white px-4 py-2 rounded-lg font-medium text-sm hover:bg-purple-700"
                >
                  Save
                </button>
                <button
                  onClick={handleModalClose}
                  className="bg-gray-500 text-white px-4 py-2 rounded-lg font-medium text-sm hover:bg-gray-400 ml-2 hover:text-purple-800"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <ToastContainer position="top-center" autoClose={5000} hideProgressBar={false} newestOnTop closeButton={true} />
    </>
  );
};

export default AdvertisementCard;
